import { useSplitTreatments } from '@splitsoftware/splitio-react';

interface UseIsDtcConciergeEnabledOptions {
  email?: string;
  phoneNumber?: string;
  appId?: string;
  appConfigId?: string;
  domain?: string;
  path?: string;
  themeName?: string;
  themeId?: string;
  visitorId?: string;
}

/**
 * Split `brandclub_dtc_concierge` targets `domain` with an exact "Is in list"
 * match. Operators can put `*.shopperos.app` in that list to cover every
 * ShopperOS host; this rewrites a real hostname under that base to the wildcard
 * token so the list entry matches.
 *
 * Uses a proper host suffix check (not substring) so
 * `shopperos.app.evil.com` does not qualify.
 */
export function resolveDtcConciergeDomainAttribute(domain: string | undefined): string | undefined {
  if (domain == null) return undefined;
  const host = domain.trim().toLowerCase().replace(/\.$/, '');
  if (!host) return domain;
  if (host === 'shopperos.app' || host.endsWith('.shopperos.app')) {
    return '*.shopperos.app';
  }
  return domain;
}

/**
 * Was this page served by the Shopify App Proxy skeleton? Read from the
 * bootstrap the server injects — the same signal the rest of the Shopify
 * surface uses, and the only one D-31 allows.
 */
function isShopifyAppProxySurface(): boolean {
  if (typeof window === 'undefined') return false;
  const bootstrap = (window as unknown as { __BC_BOOTSTRAP__?: { servingSurface?: string } }).__BC_BOOTSTRAP__;
  return bootstrap?.servingSurface === 'shopify_app_proxy';
}

export const useIsDtcConciergeEnabled = (options?: UseIsDtcConciergeEnabledOptions): boolean => {
  const attributes: Record<string, string> = {
    email: options?.email ?? '',
    phoneNumber: options?.phoneNumber ?? '',
  };

  if (options?.appId !== undefined) attributes.appId = options.appId;
  if (options?.appConfigId !== undefined) attributes.appConfigId = options.appConfigId;
  const domainAttr = resolveDtcConciergeDomainAttribute(options?.domain);
  if (domainAttr !== undefined) attributes.domain = domainAttr;
  if (options?.path !== undefined) attributes.path = options.path;
  if (options?.themeName !== undefined) attributes.themeName = options.themeName;
  if (options?.themeId !== undefined) attributes.themeId = options.themeId;
  if (options?.visitorId !== undefined) attributes.visitorId = options.visitorId;

  const { treatments } = useSplitTreatments({
    names: ['brandclub_dtc_concierge'],
    attributes,
  });

  // On the Shopify App Proxy surface the INSTALL is the enablement.
  //
  // This flag targets `domain` with an exact "is in list" match, which works for
  // the handful of front-room brands we onboard by hand — and cannot work for a
  // public App Store app, where any store may install at any moment and nobody
  // is there to add `their-store.myshopify.com` to a Split list. Left gated, the
  // merchant's own concierge page redirected to `/` and rendered a blank screen
  // (2026-08-19), while the bubble on the same store worked, because only this
  // page consults the flag.
  //
  // The signal is the injected bootstrap, never DOM shape (D-31).
  if (isShopifyAppProxySurface()) return true;

  return treatments?.brandclub_dtc_concierge?.treatment === 'on';
};
